import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, Star, Award, Users, CheckCircle, Calendar, Shield, Trophy } from 'lucide-react';

const BestDentistFortWorth = () => {
  const awards = [
    { year: '2025', title: 'Best of Fort Worth', source: 'Fort Worth Magazine' },
    { year: '2024', title: 'Top Dentist', source: 'Texas Monthly' },
    { year: '2023', title: 'Patients\' Choice', source: 'Healthgrades' },
    { year: '2022', title: 'Excellence Award', source: 'DFW Dental Society' },
  ];

  const reviews = [
    { name: 'Sarah M.', rating: 5, text: 'Best dental experience I\'ve ever had! Dr. Mund is incredibly skilled and the staff is so friendly.' },
    { name: 'Michael R.', rating: 5, text: 'I\'ve been to many dentists, but Fort Worth Dental is by far the best. Highly recommend!' },
    { name: 'Jennifer L.', rating: 5, text: 'Finally found a dentist I trust. The care here is exceptional and they explain everything.' },
  ];

  const whyBest = [
    { title: 'Board-Certified Specialists', desc: 'Dr. Arthur Mund is a Diplomate of ABOI with 25+ years experience in implants.' },
    { title: 'Advanced Technology', desc: 'State-of-the-art equipment including digital X-rays, LANAP laser, and 3D imaging.' },
    { title: '500+ 5-Star Reviews', desc: 'Consistently rated the top dental practice in Fort Worth by patients.' },
    { title: 'Comprehensive Care', desc: 'From general dentistry to complex implants, all services under one roof.' },
    { title: 'Patient-First Approach', desc: 'We listen to your concerns and create personalized treatment plans.' },
    { title: 'Flexible Financing', desc: 'Multiple payment options and insurance acceptance to fit your budget.' },
  ];

  return (
    <>
      <Helmet>
        <title>Best Dentist in Fort Worth, TX | Award-Winning Fort Worth Dental</title>
        <meta name="description" content="Looking for the best dentist in Fort Worth? Fort Worth Dental is voted #1 with 500+ 5-star reviews. Award-winning care from board-certified specialists. Call (817) 985-3337!" />
        <meta name="keywords" content="best dentist fort worth, best dentist in fort worth, top dentist fort worth, best dental clinic fort worth, award winning dentist fort worth" />
        <link rel="canonical" href="https://fortworthdental.com/best-dentist-fort-worth" />
        <meta property="og:title" content="Best Dentist in Fort Worth, TX | Fort Worth Dental" />
        <meta property="og:description" content="Award-winning dental care with 500+ 5-star reviews. Voted best dentist in Fort Worth." />
        <meta property="og:url" content="https://fortworthdental.com/best-dentist-fort-worth" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Dentist",
            "name": "Fort Worth Dental",
            "description": "Award-winning best dentist in Fort Worth, TX with 500+ 5-star reviews.",
            "url": "https://fortworthdental.com/best-dentist-fort-worth",
            "telephone": "+1-817-985-3337",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "4620 Citylake Blvd W",
              "addressLocality": "Fort Worth",
              "addressRegion": "TX",
              "postalCode": "76132"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "bestRating": "5",
              "reviewCount": "500"
            },
            "award": ["Best of Fort Worth 2025", "Top Dentist Texas Monthly 2024"]
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-yellow-500 via-yellow-600 to-orange-500 text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-6">
              <Trophy className="w-5 h-5" />
              <span>Voted Best Dentist in Fort Worth</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Fort Worth's <span className="text-gray-900">#1 Rated</span> Dental Practice
            </h1>
            <p className="text-xl md:text-2xl mb-8">
              500+ 5-Star Reviews | Award-Winning Care | 25+ Years of Excellence
            </p>
            <div className="flex justify-center gap-1 mb-8">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-8 h-8 fill-white text-white" />
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:817-985-3337"
                className="inline-flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-bold py-4 px-8 rounded-lg text-lg transition"
              >
                <Phone className="w-6 h-6" />
                (817) 985-3337
              </a>
              <Link
                to="/appointments/"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-yellow-600 font-bold py-4 px-8 rounded-lg text-lg transition"
              >
                <Calendar className="w-6 h-6" />
                Book Your Visit
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Awards */}
      <section className="py-12 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            {awards.map((award, idx) => (
              <div key={idx} className="text-center">
                <div className="text-yellow-500 font-bold text-lg">{award.year}</div>
                <div className="font-semibold">{award.title}</div>
                <div className="text-gray-400 text-sm">{award.source}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why We're The Best */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-800">
            What Makes Us the <span className="text-yellow-600">Best Dentist in Fort Worth</span>
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            We don't just claim to be the best—our patients, awards, and results prove it.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whyBest.map((item, idx) => (
              <div key={idx} className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-lg text-gray-800 mb-1">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-800">
            Why Patients Say We're the Best
          </h2>
          <p className="text-center text-gray-600 mb-12">
            Real reviews from real patients
          </p>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {reviews.map((review, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex gap-1 mb-4">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{review.text}"</p>
                <div className="font-semibold text-gray-800">— {review.name}</div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/testimonials/" className="text-yellow-600 hover:text-yellow-700 font-semibold">
              Read All 500+ Reviews →
            </Link>
          </div>
        </div>
      </section>

      {/* Meet The Doctors */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
            Meet Fort Worth's Best Dentists
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <div className="w-32 h-32 bg-teal-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Users className="w-16 h-16 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Dr. Arthur Mund, DDS</h3>
              <p className="text-yellow-600 font-semibold mb-2">Diplomate, ABOI</p>
              <p className="text-gray-600 text-sm">
                Board-certified implant specialist with 25+ years experience. 
                Known for his gentle approach and exceptional results.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl text-center">
              <div className="w-32 h-32 bg-teal-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Users className="w-16 h-16 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Dr. David Razo, DDS</h3>
              <p className="text-yellow-600 font-semibold mb-2">General & Cosmetic Dentistry</p>
              <p className="text-gray-600 text-sm">
                Expert in cosmetic dentistry, Invisalign, and comprehensive family dental care.
              </p>
            </div>
          </div>
          <div className="text-center mt-8">
            <Link to="/meet-our-team/" className="text-teal-600 hover:text-teal-700 font-semibold">
              Meet Our Full Team →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-yellow-500 to-orange-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Experience the Best Dental Care in Fort Worth
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied patients. Schedule your appointment with Fort Worth's top-rated dentist today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:817-985-3337"
              className="inline-flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-bold py-4 px-8 rounded-lg text-lg transition"
            >
              <Phone className="w-6 h-6" />
              Call (817) 985-3337
            </a>
            <Link
              to="/appointments/"
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-yellow-600 font-bold py-4 px-8 rounded-lg text-lg transition"
            >
              Schedule Online
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default BestDentistFortWorth;
